import React, { memo, useContext } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool } from 'prop-types'

import { TURN_STATUS } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import Composer from 'Pods/ThreeDStudio/Shared/Composer/Composer'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import Conversation from './Components/Conversation/Conversation'
import GenerationSettings from './Components/GenerationSettings/GenerationSettings'
import ModelSelector from './Components/ModelSelector/ModelSelector'
import NewImageNote from './Components/NewImageNote/NewImageNote'
import PrivateToggle from './Components/PrivateToggle/PrivateToggle'
import StyleButton from './Components/StyleButton/StyleButton'
import useCreateTab from './useCreateTab'

const composerControls = (
  <>
    <ModelSelector />
    <StyleButton />
    <PrivateToggle />
    <GenerationSettings />
  </>
)

// isFullWidth renders the tab as the Image Generator page rather than inside the assistant
// panel: the same conversation and composer, laid out across the whole content area.
function CreateTab({ isFullWidth = false }) {
  const t = useTranslation()
  const { twoDImagesIdeationFlowEnabled } = useContext(ThreeDStudioPageContext)
  const {
    prompt,
    attachments,
    messages,
    tags,
    isGenerating,
    isAccessPending,
    isUploadDisabled,
    fileInputRef,
    handlePromptChange,
    handleTagClick,
    handleUploadClick,
    handleRemoveAttachment,
    handleFilesChange,
    handleSend,
    handleRegenerate,
    handleGenerateAgain,
    handleApply,
    handleEdit,
  } = useCreateTab({ isFullWidth })

  // A send while the access tier is still hydrating cannot be judged, so the guard in
  // handleSend would swallow it; the button waits instead.
  const isSendDisabled = isGenerating || isAccessPending || !prompt.trim()
  const hasGeneratedImage = messages.some(message => message.status === TURN_STATUS.DONE && message.result)

  return (
    <div
      className={classnames(
        'image-assistant-create-tab__create',
        isFullWidth && 'image-assistant-create-tab__create--full-width',
      )}
    >
      <Conversation
        messages={messages}
        isGenerating={isGenerating}
        onApply={handleApply}
        onRegenerate={handleRegenerate}
        onGenerateAgain={handleGenerateAgain}
        onEdit={handleEdit}
        isFullWidth={isFullWidth}
      />
      <Composer
        prompt={prompt}
        attachments={attachments}
        tags={tags}
        fileInputRef={fileInputRef}
        customClass={isFullWidth ? 'image-assistant-create-tab__composer--full-width' : ''}
        sendLabel={t('imageAssistantPanel.createTab.composer.send')}
        notice={hasGeneratedImage ? <NewImageNote /> : null}
        controls={twoDImagesIdeationFlowEnabled ? composerControls : null}
        uploadLabel={twoDImagesIdeationFlowEnabled ? t('imageAssistantPanel.createTab.composer.addReferences') : null}
        isSendDisabled={isSendDisabled}
        isUploadDisabled={isUploadDisabled}
        onPromptChange={handlePromptChange}
        onTagClick={handleTagClick}
        onUploadClick={handleUploadClick}
        onRemoveAttachment={handleRemoveAttachment}
        onFilesChange={handleFilesChange}
        onSend={handleSend}
      />
    </div>
  )
}

CreateTab.propTypes = {
  isFullWidth: bool,
}

export default memo(CreateTab)
